import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { TalkRoomNote } from '../../types';
import Button from '../common/Button';
import Input from '../common/Input';
import Modal from '../common/Modal';

interface NotesTabProps {
  roomId: string;
  currentUserId: string;
}

export default function NotesTab({ roomId, currentUserId }: NotesTabProps) {
  const [notes, setNotes] = useState<TalkRoomNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<TalkRoomNote | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // フォーム
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState<Array<{ name: string; url: string; type: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteNoteTarget, setDeleteNoteTarget] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchNotes();
  }, [roomId]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const data = await api.get<{ items: TalkRoomNote[] }>(`/api/talkrooms/${roomId}/notes`);
      setNotes(data.items || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || saving) return;

    setSaving(true);
    try {
      await api.post(`/api/talkrooms/${roomId}/notes`, {
        title: title.trim(),
        content,
        attachments,
      });
      setTitle('');
      setContent('');
      setAttachments([]);
      setShowCreateModal(false);
      fetchNotes();
    } catch (error) {
      console.error('Failed to create note:', error);
      toast.error('ノートの作成に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedNote || !title.trim() || saving) return;

    setSaving(true);
    try {
      await api.put(`/api/talkrooms/${roomId}/notes/${selectedNote.id}`, {
        title: title.trim(),
        content,
        attachments,
      });
      setIsEditing(false);
      setSelectedNote(null);
      fetchNotes();
    } catch (error) {
      console.error('Failed to update note:', error);
      toast.error('ノートの更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    setDeleteNoteTarget(noteId);
  };

  const confirmDeleteNote = async () => {
    if (!deleteNoteTarget) return;
    try {
      await api.delete(`/api/talkrooms/${roomId}/notes/${deleteNoteTarget}`);
      if (selectedNote?.id === deleteNoteTarget) {
        setSelectedNote(null);
      }
      fetchNotes();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('ノートの削除に失敗しました');
    } finally {
      setDeleteNoteTarget(null);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const newFiles: Array<{ name: string; url: string; type: string }> = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > 20 * 1024 * 1024) {
          toast.error(`${file.name}: ファイルサイズは20MB以下にしてください`);
          continue;
        }
        const url = await api.uploadFile(file, { entityType: 'talkroom-note', entityId: roomId });
        newFiles.push({ name: file.name, url, type: file.type });
      }
      setAttachments((prev) => [...prev, ...newFiles]);
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('ファイルのアップロードに失敗しました');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const openNoteDetail = (note: TalkRoomNote) => {
    setSelectedNote(note);
    setTitle(note.title);
    setContent(note.content || '');
    setAttachments(note.attachments || []);
    setIsEditing(false);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* ヘッダー */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-white">ノート</h3>
        <Button onClick={() => { setShowCreateModal(true); setTitle(''); setContent(''); setAttachments([]); }} size="sm">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          新規作成
        </Button>
      </div>

      {/* ノート一覧 */}
      <div className="flex-1 overflow-y-auto p-4">
        {notes.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-12">
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>ノートがありません</p>
            <p className="text-sm mt-1">新規作成ボタンからノートを追加してください</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {notes.map((note) => (
              <button
                key={note.id}
                onClick={() => openNoteDetail(note)}
                className="text-left p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg hover:shadow-md transition-shadow"
              >
                <h4 className="font-medium text-gray-900 dark:text-white truncate">{note.title}</h4>
                {note.content && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{note.content}</p>
                )}
                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                  <span>{note.createdByName}</span>
                  <span>{formatDate(note.updatedAt)}</span>
                </div>
                {note.attachments && note.attachments.length > 0 && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-blue-600">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    {note.attachments.length}件
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 新規作成モーダル */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="新しいノート" size="lg">
        <form onSubmit={handleCreateNote} className="space-y-4">
          <Input
            label="タイトル"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="ノートのタイトル"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">内容</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white resize-none"
              placeholder="ノートの内容を入力..."
            />
          </div>

          {/* 添付ファイル */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">添付ファイル</label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              size="sm"
            >
              {uploading ? 'アップロード中...' : 'ファイルを追加'}
            </Button>
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((att, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                    <span className="text-sm truncate">{att.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)}>
              キャンセル
            </Button>
            <Button type="submit" disabled={saving || !title.trim()}>
              {saving ? '作成中...' : '作成'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ノート削除確認モーダル */}
      <Modal isOpen={!!deleteNoteTarget} onClose={() => setDeleteNoteTarget(null)} title="ノートの削除">
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">このノートを削除しますか？</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteNoteTarget(null)}>キャンセル</Button>
            <Button variant="danger" onClick={confirmDeleteNote}>削除</Button>
          </div>
        </div>
      </Modal>

      {/* ノート詳細モーダル */}
      <Modal
        isOpen={!!selectedNote}
        onClose={() => { setSelectedNote(null); setIsEditing(false); }}
        title={isEditing ? 'ノートを編集' : selectedNote?.title || ''}
        size="lg"
      >
        {selectedNote && (
          isEditing ? (
            <form onSubmit={handleUpdateNote} className="space-y-4">
              <Input
                label="タイトル"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">内容</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">添付ファイル</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  size="sm"
                >
                  {uploading ? 'アップロード中...' : 'ファイルを追加'}
                </Button>
                {attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                        <span className="text-sm truncate">{att.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="secondary" onClick={() => setIsEditing(false)}>
                  キャンセル
                </Button>
                <Button type="submit" disabled={saving || !title.trim()}>
                  {saving ? '保存中...' : '保存'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-500">
                作成者: {selectedNote.createdByName} | 更新: {formatDate(selectedNote.updatedAt)}
              </div>
              <div className="prose dark:prose-invert max-w-none">
                <p className="whitespace-pre-wrap">{selectedNote.content || '(内容なし)'}</p>
              </div>

              {selectedNote.attachments && selectedNote.attachments.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2">添付ファイル</h4>
                  <div className="space-y-1">
                    {selectedNote.attachments.map((att, idx) => (
                      <a
                        key={idx}
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <span className="text-sm text-blue-600 hover:underline">{att.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-4 border-t dark:border-gray-700">
                <Button
                  variant="danger"
                  onClick={() => handleDeleteNote(selectedNote.id)}
                  size="sm"
                >
                  削除
                </Button>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setSelectedNote(null)}>
                    閉じる
                  </Button>
                  <Button onClick={() => setIsEditing(true)}>
                    編集
                  </Button>
                </div>
              </div>
            </div>
          )
        )}
      </Modal>
    </div>
  );
}
